import React from "react";
import { Tag, Tooltip } from "antd";
import { useBankConnected } from "../socketMessageState/BankMessagesContext";
import useOverlaySources, { OverlaySource } from "./useOverlaySources";

/**
 * A badge is at most as wide as the card: a long source name is cut with an
 * ellipsis, and the tooltip carries all of it.
 */
const BADGE_STYLE: React.CSSProperties = {
  marginTop: 4,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "top",
};

/** One source bank's badge: nothing while it is connected. */
function SourceOffline({ source }: { source: OverlaySource }) {
  const connected = useBankConnected(source.key);
  // Not yet heard from counts as offline: until the source says it is
  // connected, nothing it drew here can be trusted to be current.
  if (connected === true) return null;
  return (
    <Tooltip
      placement="topLeft"
      title={`${source.name} draws its state on this bank and is offline: what it drew here may be stale.`}
    >
      <Tag color="warning" data-testid="overlay-source-offline" style={BADGE_STYLE}>
        {source.name} offline
      </Tag>
    </Tooltip>
  );
}

/**
 * A badge on a bank's card for each bank drawing on it (a bank overlay) that is
 * offline. The overlaid state stays drawn — greyed in its floor cell — because
 * clearing a secured floor's padlock when its input module stops reporting
 * would show the floor unsecured on no evidence; the badge says why it may be
 * stale. The bank's own connection status is untouched.
 */
export default function OverlaySourcesOffline({ bankId }: { bankId: string | null }) {
  const sources = useOverlaySources(bankId);
  if (sources.length === 0) return null;
  return (
    <>
      {sources.map((source) => (
        <SourceOffline key={source.key} source={source} />
      ))}
    </>
  );
}
